import './PacingPane.scss';
import { ReactElement, ReactNode } from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import cn from 'classnames';
import { DataCard } from '@Components/DataCard/DataCard';
import { EmptyState } from '@Components/EmptyState';
import { InteractiveDiv } from '@Components/InteractiveEl';

interface PacingPaneProps<T extends { key: string }> {
  // Rendered above the rows; a drilled-in page hides them by leaving them out.
  title?: string;
  kpis?: { label: string; value: string }[];
  // A "Total" line drawn with the same renderer as the rows.
  summary?: ReactNode;
  controls?: ReactNode;
  items?: T[];
  labelRenderer: (item: T) => ReactNode;
  // Optional so a condensed pane can show its rows' labels alone.
  lineItemRenderer?: (item: T) => ReactNode;
  selectedKey?: string;
  onSelect: (selection?: string) => void;
}

// A scrolling list of selectable rows, each a label plus an optional bar. Clicking the selected row
// clears the selection. Lay panes out with the .pacing-panes classes in PacingPane.scss, whose
// .condensed variants shrink a pane to its selected row while the next one drills in.
export const PacingPane = <T extends { key: string }>({
  title,
  kpis,
  summary,
  controls,
  items = [],
  labelRenderer,
  lineItemRenderer,
  selectedKey,
  onSelect
}: PacingPaneProps<T>): ReactElement => (
  <div className='pacing-pane'>
    {title && <div className='pacing-pane-title'>{title}</div>}
    {kpis && (
      <div className='pacing-pane-kpis'>
        {kpis.map(({ label, value }) => (
          <DataCard key={label} label={label} value={value} />
        ))}
      </div>
    )}
    {summary && (
      <div className='pacing-pane-summary-line'>
        <div className='pacing-pane-summary-caption'>Total</div>
        <div className='pacing-pane-line-item'>{summary}</div>
      </div>
    )}
    {controls && <div className='pacing-pane-controls'>{controls}</div>}
    <div className='pacing-pane-lines'>
      {items.length === 0 && <EmptyState title='No pacing data to show' />}
      {items.map(item => {
        const isSelected = selectedKey === item.key;
        return (
          <InteractiveDiv
            key={item.key}
            className={cn('pacing-pane-row', { selected: isSelected })}
            onClick={() => onSelect(isSelected ? undefined : item.key)}
          >
            <div className='pacing-pane-row-contents'>
              <OverlayTrigger placement='top-start' overlay={<Tooltip>{item.key}</Tooltip>}>
                <div className='pacing-pane-row-label'>{labelRenderer(item)}</div>
              </OverlayTrigger>
              {lineItemRenderer && (
                <div className='pacing-pane-line-item'>{lineItemRenderer(item)}</div>
              )}
            </div>
          </InteractiveDiv>
        );
      })}
    </div>
  </div>
);
